import { action, observable } from "mobx";
import RequestsProcessor from "@src/Tools/AxiosRequestsProcessor";
import { IValues } from "@src/Interfaces/Entity/Values";
import { LocationShort } from "@src/Interfaces/Entity/Location";
import { ApplicationLink } from "@src/Interfaces/Entity/ApplicationLink";
import { EnvironmentValues } from "@src/Interfaces/Entity/EnvironmentValues";
import { IValuesStore, INumberDict, IStringDict, TState, IThemes, IRoles } from "./types";
import { Values } from "@src/Tools/Values";
import ThemeHelperFactory from "../../../public/ThemeHelperFactory";
import Stores from "..";
import { IApiResponse } from "../../Interfaces/Entity/ApiResponse";

const requestsProcessor: RequestsProcessor = new RequestsProcessor();
var themeHelper: any;

class ValuesStore implements IValuesStore {
  @observable language: string = "";
  @observable localization: INumberDict;
  @observable auditLogSeverityColors: INumberDict;
  @observable common: IStringDict;
  @observable allowedHtmlTags: string[];
  @observable dataReadAndRedirectAttribute: string;
  @observable locationTypes: INumberDict;
  @observable roles: IRoles;
  @observable states: TState;
  @observable location: string = "";
  @observable loginLocations: LocationShort[] = [];
  @observable version: string = "";
  @observable themeTypes: IThemes;
  @observable syncActions: INumberDict;
  @observable auditLogSeverities: INumberDict;
  @observable auditLogTypes: INumberDict;
  @observable auditLogRecordsPeriod: string;
  @observable oAuthProviderTypes: INumberDict;
  @observable authProtocolTypes: INumberDict;
  @observable metaDataTypes: INumberDict;
  @observable filesPath: string = "";
  @observable application: any[] = [];

  @observable isNightTheme: boolean = false;

  @observable PWAEvent: any;
  @observable isPWAModalOpened: boolean = false;

  @observable showRequestProgress: boolean = false;
  @observable isRequestProgressForced: boolean = false;
  @observable isInitialSynchronizationConfigured: boolean = false;
  @observable isSyncLogsVisible: boolean = false;
  @observable isEmailSettingsConfigured: boolean = false;
  @observable requireInitialSynchronization: boolean = false;
  @observable canManageSystemSettings: boolean = false;
  @observable applicationPossibilities: any;
  @observable notificationTypes: INumberDict;
  @observable launcherUrl: string;
  @observable defaultSeededOrganizationName: string;
  @observable tenant: string;

  @observable applicationId: number = -1;
  @observable applicationName: string = "";
  @observable versionTag: string = "";
  @observable versionHash: string = "";
  @observable gitBranch: string = "";
  @observable knownApplications: INumberDict;
  @observable logTypes: INumberDict;
  @observable fileTypes: INumberDict;
  @observable applicationLink: ApplicationLink[];
  @observable webhookActions: INumberDict;
  @observable webhookEntities: IStringDict;
  @observable knownApplicationUrls: IStringDict;

  @action.bound
  async getValues(tenant?: string) {
    try {
      let requestUrl = "values";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
      const response:IApiResponse<IValues> = await requestsProcessor.get<IApiResponse<IValues>>(requestUrl, "GetValues");
      const responseData: IValues = response.data;

      this.language = responseData.language;
      this.localization = (responseData.localization as unknown) as INumberDict;
      Values.localization = responseData.localization;
      this.common = responseData.common;
      this.allowedHtmlTags = (responseData.allowedHtmlTags as unknown) as string[];
      this.dataReadAndRedirectAttribute = responseData.dataReadAndRedirectAttribute;
      this.auditLogSeverityColors = (responseData.auditLogSeverityColors as unknown) as INumberDict;
      this.locationTypes = (responseData.locationTypes as unknown) as INumberDict;
      this.roles = (responseData.roles as unknown) as IRoles;
      this.states = (responseData.states as unknown) as TState;
      this.location = responseData.location;
      this.version = responseData.version;
      this.themeTypes = (responseData.themeTypes as unknown) as IThemes;
      this.syncActions = (responseData.syncActions as unknown) as INumberDict;
      this.auditLogSeverities = (responseData.auditLogSeverities as unknown) as INumberDict;
      this.auditLogTypes = (responseData.auditLogTypes as unknown) as INumberDict;
      this.auditLogRecordsPeriod = (responseData.auditLogRecordsPeriod as unknown) as string;
      this.oAuthProviderTypes = (responseData.oAuthProviderTypes as unknown) as INumberDict;
      this.authProtocolTypes = (responseData.authProtocolTypes as unknown) as INumberDict;
      this.metaDataTypes = (responseData.metaDataTypes as unknown) as INumberDict;
      this.applicationPossibilities = responseData.applicationPossibilities;
      this.isInitialSynchronizationConfigured = responseData.isInitialSynchronizationConfigured;
      this.isSyncLogsVisible = responseData.isSyncLogsVisible;
      this.isEmailSettingsConfigured = responseData.isEmailSettingsConfigured;
      this.requireInitialSynchronization = responseData.requireInitialSynchronization;
      this.canManageSystemSettings = responseData.canManageSystemSettings;
      this.notificationTypes = (responseData.notificationTypes as unknown) as INumberDict;
      this.launcherUrl = (responseData.launcherUrl as unknown) as string;
      this.defaultSeededOrganizationName = (responseData.defaultSeededOrganizationName as unknown) as string;
      this.tenant = (responseData.tenant as unknown) as string;

      this.applicationId = responseData.applicationId;
      this.applicationName = responseData.applicationName;
      this.versionTag = responseData.versionTag;
      this.versionHash = responseData.versionHash;
      this.gitBranch = responseData.gitBranch;
      this.knownApplications = (responseData.knownApplications as unknown) as INumberDict;
      this.logTypes = (responseData.logTypes as unknown) as INumberDict;
      this.fileTypes = (responseData.fileTypes as unknown) as INumberDict;

      if (!themeHelper) {
          themeHelper = new ThemeHelperFactory().GetThemeHelper(new URL(location.href).origin, (e: any) => {
          this.isNightTheme = e.IsDarkTheme;
          Stores.themes.getCurrentTheme(this.themeTypes.Application);
        });
      }
      this.isNightTheme = themeHelper.GetTheme().IsDarkTheme;

      document.title = Values.getLocalizationValue("applicationName");

      this.webhookActions = (responseData.webhookActions as unknown) as INumberDict;
      this.webhookEntities = (responseData.webhookEntities as unknown) as IStringDict;

      return responseData;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getApplications(appId: number) {
    try {
      const response: EnvironmentValues = await requestsProcessor.get<EnvironmentValues>(
        appId != null ? `Environment/Get?applicationToSkip=${appId}` : `Environment/Get`,
        "GetEnvironment"
      );
      return response.applications;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getUserManagementUrl(): Promise<string> {
    try {
      const response: string = await requestsProcessor.get<string>(
        `Environment/GetUserManagementUrl`,
        "GetUserManagementUrl"
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async GetKnownApplicationUrls(): Promise<IStringDict> {
    try {
      const response: IStringDict = await requestsProcessor.get<IStringDict>(
        `Environment/GetKnownApplicationUrls`,
        "GetKnownApplicationUrls"
      );
      this.knownApplicationUrls = response;
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  async getSwaggerUrl(serviceOnly: boolean, authOnly?: boolean): Promise<string> {
    try {
      const response: string = await requestsProcessor.get<string>(
        `Environment/GetSwaggerUrl?serviceOnly=${serviceOnly}&oauthOnly=${Boolean(authOnly)}`,
        "GetSwaggerUrl"
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  toggleThemeVariant(isNightTheme: boolean) {
      this.isNightTheme = isNightTheme;
      themeHelper.SetTheme(isNightTheme);
  }

  @action.bound
  async getLocationsForLogin(tenant?: string) {
    try {
      let requestUrl = "locations/available";
      if (tenant) {
        requestUrl += `?tenant=${tenant}`;
      }
      const response: IApiResponse<LocationShort[]> = await requestsProcessor.get<IApiResponse<LocationShort[]>>(requestUrl, "GetAvailableForLogin");
      this.loginLocations = response.data;
      return response.data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  handleRequestProcess(state: boolean) {
    if (this.isRequestProgressForced) {
      return;
    } else if (this.showRequestProgress === state) {
      return;
    } else if (state && this.showRequestProgress !== state) {
      this.showRequestProgress = true;
    } else if (!state && this.showRequestProgress !== state) {
      this.showRequestProgress = false;
    } else {
      console.error("possible condition race or error");
      this.showRequestProgress = false;
    }
  }

  @action.bound
  forceRequestProcess(state: boolean) {
    if (state) {
      this.isRequestProgressForced = true;
    } else {
      this.isRequestProgressForced = false;
    }
    this.showRequestProgress = state;
  }

  @action.bound
  setPWAEvent(e: any) {
    this.PWAEvent = e;
    const PWAMessage = localStorage.getItem("PwaMessageShowed");
    if (PWAMessage !== "yes") {
      this.isPWAModalOpened = true;
    }
  }

  @action.bound
  togglePWAModal() {
    this.isPWAModalOpened = !this.isPWAModalOpened;
  }
}

export default ValuesStore;
